import { useEffect, useState } from "react";
import { Wallet, IndianRupee, CalendarClock, Percent, TrendingUp, CheckCircle2 } from "lucide-react";
import { api, inr, fmtDate } from "@/lib/api";
import { Skeleton, StatusBadge, Progress, EmptyState, PenguinIcon } from "@/components/common";
import {
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const TEAL = "#14B8A6", NAVY = "#0B132B", COLORS = ["#14B8A6", "#0B132B", "#6366F1", "#F59E0B", "#EF4444"];
const inrShort = (n) => { n = Number(n || 0); if (Math.abs(n) >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr"; if (Math.abs(n) >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L"; return inr(n); };

function Kpi({ label, value, icon: Icon, accent = TEAL, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`ins-kpi-${label}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: accent + "1a", color: accent }}><Icon size={16} /></span>
      </div>
      <p className="mt-2 font-display text-xl font-bold text-[#0B132B] sm:text-2xl">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
function Panel({ title, subtitle, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div><h3 className="font-display font-bold text-[#0B132B]">{title}</h3>{subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function MyInsights() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState("ALL");
  useEffect(() => {
    setD(null);
    api.get(`/insights?loan_id=${sel}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [sel]);

  if (!d) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div><Skeleton className="h-72" /></div>;
  const o = d.overview;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">My Financial Overview</h1><p className="text-sm text-slate-500">Your personal repayment analytics.</p></div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-500">Loan</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="insights-loan-select"
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-[#14B8A6]">
            <option value="ALL">All loans</option>
            {d.loans.map((l) => <option key={l.id} value={l.id}>{l.loan_number} — {l.lender}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total Loan Amount" value={inrShort(o.total_loan_amount)} icon={Wallet} accent="#6366F1" />
        <Kpi label="Total Paid" value={inrShort(o.total_paid)} icon={IndianRupee} accent="#10B981" />
        <Kpi label="Outstanding" value={inrShort(o.outstanding)} icon={PenguinIcon} accent="#F59E0B" />
        <Kpi label="Next EMI" value={inr(o.monthly_emi)} icon={CalendarClock} sub={o.next_due_date ? `Due ${fmtDate(o.next_due_date)}` : "No dues 🎉"} />
        <Kpi label="Repayment Progress" value={`${o.repayment_progress}%`} icon={Percent} accent="#10B981" sub={`${o.paid_emis}/${o.total_emis} EMIs`} />
        <Kpi label="Payment Success" value={`${o.payment_success_rate}%`} icon={CheckCircle2} accent="#10B981" />
        <Kpi label="Active Loans" value={o.active_loans} icon={Wallet} sub={`${o.total_loans} total`} />
        <Kpi label="Upcoming EMIs" value={d.upcoming.length} icon={TrendingUp} accent="#0EA5E9" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Monthly Payment Trend" subtitle="Amount paid per month" className="lg:col-span-2">
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
              <AreaChart data={d.trend} margin={{ left: 4, right: 8 }}>
                <defs><linearGradient id="ig" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={TEAL} stopOpacity={0.32} /><stop offset="100%" stopColor={TEAL} stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={44} tickFormatter={(v) => inrShort(v).replace("₹", "")} />
                <Tooltip formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="paid" stroke={TEAL} strokeWidth={2.5} fill="url(#ig)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Paid vs Outstanding">
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
              <PieChart>
                <Pie data={d.paid_vs_outstanding} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="82%" paddingAngle={2}>
                  <Cell fill="#10B981" /><Cell fill="#F59E0B" />
                </Pie>
                <Tooltip formatter={(v) => inr(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Panel title="Loan-wise Progress" subtitle="Repayment progress per loan">
        {d.loan_progress.length === 0 ? <EmptyState title="No loans" desc="Your loans will appear here." /> : (
          <div className="space-y-4">
            {d.loan_progress.map((l) => (
              <div key={l.loan_number} data-testid={`ins-loan-${l.loan_number}`} className="rounded-lg border border-slate-100 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><p className="font-mono text-xs font-semibold text-[#14B8A6]">{l.loan_number}</p><p className="font-medium text-[#0B132B]">{l.lender} <span className="text-xs text-slate-400">{l.loan_type}</span></p></div>
                  <div className="flex items-center gap-3 text-sm"><span className="text-slate-500">EMI {inr(l.emi)}</span><span className="font-semibold text-[#0B132B]">{inr(l.outstanding)} left</span><StatusBadge status={l.status} /></div>
                </div>
                <div className="mt-3"><div className="mb-1 flex justify-between text-xs text-slate-500"><span>{l.progress}% paid</span></div><Progress value={l.progress} /></div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Payment Status" subtitle="Success · Failed · Pending">
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 200 }}>
              <PieChart>
                <Pie data={d.status_breakdown} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={2}>
                  {d.status_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Upcoming EMIs" subtitle="Next dues" className="lg:col-span-2">
          {d.upcoming.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No upcoming EMIs 🎉</p> : (
            <div className="space-y-2">
              {d.upcoming.map((u, i) => (
                <div key={i} className="flex items-center justify-between rounded-md bg-slate-50 p-3 text-sm">
                  <div><p className="font-medium text-[#0B132B]">{u.lender} <span className="ml-1 font-mono text-xs text-slate-400">{u.loan_number}</span></p><p className="text-xs text-slate-400">{fmtDate(u.due_date)}</p></div>
                  <span className="font-semibold text-[#0B132B]">{inr(u.emi)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {d.selected_loan !== "ALL" && d.timeline.length > 0 && (
        <Panel title="EMI Timeline" subtitle="Schedule for the selected loan">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>{["#", "Due Date", "EMI Amount", "Outstanding Balance", "Status", "Paid On"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.timeline.map((r) => (
                  <tr key={r.emi_no} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-500">{r.emi_no}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(r.due_date)}</td>
                    <td className="px-4 py-2.5 font-semibold text-[#0B132B]">{inr(r.emi)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{inr(r.remaining)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2.5 text-slate-500">{r.paid_date ? fmtDate(r.paid_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
